import { all, takeLatest, put, call, select } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { createOrganizationsToRisks } from '../organizationsToRisks/slice';
import { onGetPolicies } from '../policies/api';
import { createPoliciesToRisks } from '../policiesToRisks/slice';
import { reloadRisks } from '../risks/slice';
import {
  getWorklistRiskMessages,
  getWorklistRiskRecommendations,
  getWorklistRiskUnreadSince,
  getWorklistRiskReminders
} from '../worklistRisks/slice';
import renderNotification from '../../helpers/notifications/renderNotification';
import { renderSuccessNotificationWithRedirect } from '../../helpers/notifications/renderSuccessNotificationWithRedirect';
import { NOTIFICATION_MESSAGES } from '../../localization/Notifications/dictionary';
import { assignGeneralMessage } from '../generalMessages/slice';
import { selectRisk } from './selectors';

import { onGetRisk, onGetRiskHistory, onGetRiskOrganizations, onCreateRisk, onUpdateRisk, onDeleteRisk } from './api';

import {
  getRisk,
  getRiskSucceeded,
  getRiskFailed,
  getRiskHistory,
  getRiskHistorySucceeded,
  getRiskHistoryFailed,
  getRiskOrganizations,
  getRiskOrganizationsSucceeded,
  getRiskOrganizationsFailed,
  getRiskConnectedPolicies,
  getRiskConnectedPoliciesSucceeded,
  getRiskConnectedPoliciesFailed,
  createRisk,
  createRiskSucceeded,
  createRiskFailed,
  updateRisk,
  updateRiskSucceeded,
  updateRiskFailed,
  deleteRisk,
  deleteRiskSucceeded,
  deleteRiskFailed
} from './slice';

const { risk_create_success, risk_delete_success } = NOTIFICATION_MESSAGES;

export function* getRiskWorker({ payload }) {
  const { riskId } = payload;
  try {
    const response = yield call(onGetRisk, riskId);
    yield put(getRiskSucceeded(response.data));
  } catch {
    yield put(getRiskFailed());
  }
}

// This saga fetches the risk history and enriches the data with UUIDs for each item and its changes.
export function* getRiskHistoryWorker({ payload }) {
  const { riskId } = payload;
  try {
    const response = yield call(onGetRiskHistory, riskId);

    const enrichedData = response.data.map((item) => ({
      ...item,
      _uuid: uuidv4(),
      changes: Array.isArray(item.changes)
        ? item.changes.map((change) => ({
            ...change,
            _uuid: uuidv4()
          }))
        : item.changes
    }));

    yield put(getRiskHistorySucceeded(enrichedData));
  } catch {
    yield put(getRiskHistoryFailed());
  }
}

export function* getRiskOrganizationsWorker({ payload }) {
  const { riskId } = payload;

  try {
    const response = yield call(onGetRiskOrganizations, riskId);
    yield put(getRiskOrganizationsSucceeded(response.data));
  } catch {
    yield put(getRiskOrganizationsFailed());
  }
}

export function* getRiskConnectedPoliciesWorker({ payload }) {
  const { riskId } = payload;
  try {
    const response = yield call(onGetPolicies, { risk_id: riskId, paging: false });
    yield put(getRiskConnectedPoliciesSucceeded(response.data));
  } catch {
    yield put(getRiskConnectedPoliciesFailed());
  }
}

export function* createRiskWorker({ payload }) {
  const { risk, linkedOrganizationIds, linkedPolicies, messageToBeAssigned, callback, navigate } = payload;
  try {
    const response = yield call(onCreateRisk, [risk]);

    if (linkedOrganizationIds && linkedOrganizationIds.length > 0) {
      const newOrganizationsToRisks = linkedOrganizationIds.map((organizationId) => ({
        risk_id: response.data[0]?.id,
        organization_id: organizationId
      }));
      yield put(createOrganizationsToRisks({ organizationsToRisks: newOrganizationsToRisks }));
    }

    if (linkedPolicies && linkedPolicies.length > 0) {
      const { id } = response.data[0];
      const policiesToRisks = linkedPolicies.map((policy) => {
        return { risk_id: id, policy_id: policy.id };
      });
      yield put(createPoliciesToRisks({ context: 'risk', policiesToRisks, showNotification: false }));
    }

    if (messageToBeAssigned) {
      yield put(
        assignGeneralMessage({
          message: messageToBeAssigned,
          entityType: 'risk',
          entityId: response.data[0].id
        })
      );
    }

    yield put(createRiskSucceeded());
    yield put(reloadRisks());
    callback?.();
    renderSuccessNotificationWithRedirect(
      risk_create_success,
      'Risiko ansehen',
      `/risk/${response.data[0].id}/main`,
      navigate
    );
  } catch {
    yield put(createRiskFailed());
  }
}

export function* updateRiskWorker({ payload }) {
  const { risk, callback } = payload;
  try {
    yield call(onUpdateRisk, [risk]);
    yield put(updateRiskSucceeded());
    callback?.();
  } catch {
    yield put(updateRiskFailed());
  }
}

export function* deleteRiskWorker({ payload }) {
  const { riskId, callback } = payload;
  try {
    yield call(onDeleteRisk, [riskId]);
    yield put(deleteRiskSucceeded());
    yield put(reloadRisks());
    callback?.();
    renderNotification({ type: 'success', message: risk_delete_success });
  } catch {
    yield put(deleteRiskFailed());
  }
}

export function* reloadRiskAndWorklistDataWorker() {
  const risk = yield select(selectRisk);
  const riskId = risk?.id;

  yield all([
    put(getRisk({ riskId })),
    put(getRiskHistory({ riskId })),
    put(getWorklistRiskMessages({ riskId })),
    put(getWorklistRiskRecommendations({ riskId })),
    put(getWorklistRiskUnreadSince({ riskId })),
    put(getWorklistRiskReminders({ riskId }))
  ]);
}

export default function* riskSaga() {
  yield all([
    takeLatest(getRisk.type, getRiskWorker),
    takeLatest(getRiskHistory.type, getRiskHistoryWorker),
    takeLatest(getRiskOrganizations.type, getRiskOrganizationsWorker),
    takeLatest(getRiskConnectedPolicies.type, getRiskConnectedPoliciesWorker),
    takeLatest(createRisk.type, createRiskWorker),
    takeLatest(updateRisk.type, updateRiskWorker),
    takeLatest(deleteRisk.type, deleteRiskWorker),
    takeLatest(updateRiskSucceeded.type, reloadRiskAndWorklistDataWorker)
  ]);
}
